import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Trash2, Loader2, Upload, X, Check, Pencil, GripVertical } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";

// type: "gallery" | "category" | "shop"
export const GalleryManager = ({ type }) => {
  const listPath = type === "gallery" ? "/gallery" : type === "shop" ? "/shop" : "/categories";
  const noun = type === "gallery" ? "photo" : type === "shop" ? "product" : "category card";
  const withPrice = type === "shop";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const fileRef = useRef(null);
  const editFileRef = useRef(null);

  const savePrice = async (id, price) => {
    try {
      await api.put(`/admin/gallery/${id}`, { price: price.trim() });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, price: price.trim() } : i)));
      toast.success("Price updated");
    } catch {
      toast.error("Failed to update price.");
    }
  };

  const persistOrder = async (newItems) => {
    setItems(newItems);
    try {
      await api.put("/admin/gallery-reorder", { type, ordered_ids: newItems.map((i) => i.id) });
      toast.success("Order saved");
    } catch {
      toast.error("Couldn't save the new order.");
      load();
    }
  };

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setDragIndex(null);
    persistOrder(reordered);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listPath);
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [listPath]);

  useEffect(() => { load(); }, [load]);

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const addItem = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Please choose an image."); return; }
    if (!newLabel.trim()) { toast.error("Please enter a label."); return; }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      await api.post("/admin/gallery", { type, img: url, label: newLabel.trim(), price: withPrice ? newPrice.trim() : "" });
      toast.success(`${noun} added`);
      setNewLabel("");
      setNewPrice("");
      setAdding(false);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm(`Remove this ${noun}?`)) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success(`${noun} removed`);
    } catch {
      toast.error("Failed to remove.");
    }
  };

  const saveLabel = async (id) => {
    if (!editLabel.trim()) { toast.error("Label can't be empty."); return; }
    try {
      await api.put(`/admin/gallery/${id}`, { label: editLabel.trim() });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, label: editLabel.trim() } : i)));
      setEditingId(null);
      toast.success("Label updated");
    } catch {
      toast.error("Failed to update.");
    }
  };

  const replaceImage = async (id, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      await api.put(`/admin/gallery/${id}`, { img: url });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, img: url } : i)));
      toast.success("Image replaced");
    } catch {
      toast.error("Failed to replace image.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={`${type}-manager`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-navy/60 text-sm tracking-[0.1em]">{items.length} {noun.toUpperCase()}{items.length === 1 ? "" : "S"}</p>
          {items.length > 1 && <p className="text-navy/40 text-xs mt-1 flex items-center gap-1.5"><GripVertical size={12} /> Hover an item and drag the handle to reorder</p>}
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} data-testid={`add-${type}-button`}
            className="flex items-center gap-2 bg-ink text-[#f2f0ea] px-5 py-2.5 text-xs font-bold tracking-[0.14em] hover:bg-navy transition-colors">
            <Plus size={15} /> ADD {noun.toUpperCase()}
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={addItem} data-testid={`add-${type}-form`} className="bg-white border border-line p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-display text-lg tracking-[0.04em]">Add new {noun}</h4>
            <button type="button" onClick={() => { setAdding(false); setNewLabel(""); setNewPrice(""); }} className="text-navy/50 hover:text-navy"><X size={18} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">{withPrice ? "PRODUCT NAME" : "LABEL / CAPTION"}</label>
              <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} data-testid={`${type}-label-input`}
                className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy" placeholder={withPrice ? "e.g. Classic T-Shirts" : "e.g. Saint Mary School"} />
            </div>
            <div>
              <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">IMAGE</label>
              <input ref={fileRef} type="file" accept="image/*" data-testid={`${type}-file-input`}
                className="w-full border border-line px-3 py-2.5 text-sm file:mr-3 file:border-0 file:bg-navy file:text-white file:px-3 file:py-1.5 file:text-xs file:cursor-pointer" />
            </div>
            {withPrice && (
              <div>
                <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">STARTING PRICE (OPTIONAL)</label>
                <div className="flex items-center border border-line focus-within:border-navy">
                  <span className="pl-4 pr-1 text-navy/50 text-sm">from $</span>
                  <input value={newPrice} onChange={(e) => setNewPrice(e.target.value)} data-testid="shop-price-input" inputMode="decimal"
                    className="w-full py-3 pr-4 text-sm outline-none" placeholder="12" />
                </div>
                <p className="text-navy/40 text-[11px] mt-1">Leave blank for no price. Only shows when "Show prices" is on.</p>
              </div>
            )}
          </div>
          <button type="submit" disabled={uploading} data-testid={`submit-${type}-button`}
            className="mt-5 flex items-center gap-2 bg-gold text-ink px-6 py-3 text-xs font-bold tracking-[0.14em] hover:bg-gold-soft transition-colors disabled:opacity-60">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {uploading ? "UPLOADING…" : "SAVE"}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-navy/50">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-line p-12 text-center text-navy/60">No {noun}s yet. Add your first one.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid={`${type}-grid`}>
          {items.map((item, idx) => (
            <div key={item.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(idx)}
              className={`bg-white border overflow-hidden group transition-all ${dragIndex === idx ? "opacity-40 border-gold" : "border-line"}`}
              data-testid={`${type}-item-${item.id}`}>
              <div className="aspect-square bg-[#e9e6dd] relative overflow-hidden">
                <img src={resolveImg(item.img)} alt={item.label} className="w-full h-full object-contain p-2" draggable={false} />
                <div
                  draggable
                  onDragStart={() => setDragIndex(idx)}
                  onDragEnd={() => setDragIndex(null)}
                  title="Drag to reorder"
                  data-testid={`drag-handle-${item.id}`}
                  className="absolute top-2 left-1/2 -translate-x-1/2 w-9 h-7 bg-ink/85 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                  <GripVertical size={15} />
                </div>
                <button onClick={() => removeItem(item.id)} data-testid={`delete-${item.id}`}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700">
                  <Trash2 size={15} />
                </button>
                <button onClick={() => editFileRef.current?.[item.id]?.click()}
                  className="absolute top-2 left-2 w-8 h-8 bg-navy text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-ink" title="Replace image">
                  <Upload size={14} />
                </button>
                <input type="file" accept="image/*" className="hidden"
                  ref={(el) => { if (!editFileRef.current) editFileRef.current = {}; editFileRef.current[item.id] = el; }}
                  onChange={(e) => replaceImage(item.id, e.target.files?.[0])} />
              </div>
              <div className="p-3">
                {editingId === item.id ? (
                  <div className="flex items-center gap-2">
                    <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} data-testid={`edit-label-input-${item.id}`}
                      className="w-full border border-line px-2 py-1.5 text-xs outline-none focus:border-navy" autoFocus />
                    <button onClick={() => saveLabel(item.id)} data-testid={`save-label-${item.id}`} className="text-green-600 hover:text-green-700"><Check size={16} /></button>
                    <button onClick={() => setEditingId(null)} className="text-navy/40 hover:text-navy"><X size={16} /></button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-navy/80 truncate">{item.label}</span>
                    <button onClick={() => { setEditingId(item.id); setEditLabel(item.label); }} data-testid={`edit-label-${item.id}`}
                      className="text-navy/40 hover:text-gold shrink-0"><Pencil size={13} /></button>
                  </div>
                )}
                {withPrice && (
                  <div className="mt-2 flex items-center border border-line focus-within:border-navy" title="Starting price">
                    <span className="pl-2 pr-1 text-navy/40 text-[11px] whitespace-nowrap">from $</span>
                    <input
                      defaultValue={item.price || ""}
                      key={item.price || "empty"}
                      data-testid={`price-input-${item.id}`}
                      inputMode="decimal"
                      placeholder="—"
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      onBlur={(e) => { if (e.target.value.trim() !== (item.price || "")) savePrice(item.id, e.target.value); }}
                      className="w-full py-1.5 pr-2 text-xs outline-none bg-transparent"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
